import {
  Card,
  CardContent,
  CardActions,
  Typography,
  IconButton,
  Chip,
  Box,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { SubAssembly } from "../../../../types/subAssembly";
import { useHasPermission } from "../../../../hooks/useHasPermission";
import { getSubAssemblyOffWingType, isAttachedSubAssembly } from "../../../../utils/subAssemblyUtils";
import { formatOffWingTypeLabel } from "../../../../utils/offWingType";
import { MINI_PACK_ICON_TOOLTIP } from "../../../../utils/miniPackExportJobUtils";

type Props = {
  subAssembly: SubAssembly;
  onEdit: (item: SubAssembly) => void;
  onDelete: (item: SubAssembly) => void;
  onMiniPack?: (item: SubAssembly) => void;
};

const formatDateValue = (value?: string | null): string => {
  if (!value) return "";
  if (value.includes("T")) return value.split("T")[0];
  return value;
};

export function SubAssemblyCard({ subAssembly, onEdit, onDelete, onMiniPack }: Readonly<Props>) {
  const canUpdate = useHasPermission("assets-assemblies", "update");
  const canDelete = useHasPermission("assets-assemblies", "delete");
  const canAccessMiniPackExport = useHasPermission("assets-mini-pack-export", "read");
  const attached = isAttachedSubAssembly(subAssembly);
  const offWingType = getSubAssemblyOffWingType(subAssembly);
  const hasOffWingType = Boolean(offWingType?.trim());
  const shopVisitReasonLabel = subAssembly.shopVisitReasonLabel?.trim();
  const airframeFamily = subAssembly.airframeFamily?.trim();

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#222",
        border: "1px solid #333",
        color: "#fff",
      }}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" sx={{ color: "hsl(19 101% 51% / 1)", mb: 1 }}>
          {subAssembly.subAssemblyType} - {subAssembly.serialNumber}
        </Typography>
        <Typography sx={{ color: "#ccc", mb: 1 }}>
          Serial: {subAssembly.serialNumber}
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, my: 1 }}>
          <Chip
            size="small"
            label={subAssembly.landingGearType}
            sx={{
              backgroundColor: "hsl(19 101% 51% / 0.2)",
              color: "#fff",
            }}
          />
          {airframeFamily && (
            <Chip
              size="small"
              label={airframeFamily}
              sx={{
                backgroundColor: "hsla(205, 70%, 42%, 0.3)",
                color: "hsl(205 85% 72%)",
                border: "1px solid hsla(205, 65%, 48%, 0.5)",
              }}
            />
          )}
          <Chip
            size="small"
            label={attached ? "Attached" : "Available"}
            sx={{
              backgroundColor: attached
                ? "hsla(142, 45%, 36%, 0.35)"
                : "hsla(205, 70%, 42%, 0.3)",
              color: attached ? "hsl(142 52% 62%)" : "hsl(205 85% 72%)",
              border: attached
                ? "1px solid hsla(142, 45%, 42%, 0.55)"
                : "1px solid hsla(205, 65%, 48%, 0.5)",
              fontWeight: 600,
            }}
          />
        </Box>

        {subAssembly.partNumber && (
          <Typography sx={{ color: "#ccc", mb: 1 }}>
            Part Number: {subAssembly.partNumber}
          </Typography>
        )}

        {subAssembly.installDate && (
          <Typography sx={{ color: "#ccc", mb: 1 }}>
            Install Date: {formatDateValue(subAssembly.installDate)}
          </Typography>
        )}

        <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #333" }}>
          {attached ? (
            <>
              {subAssembly.attachedToAirframe && (
                <Typography sx={{ color: "#ccc", mb: 1 }}>
                  Attached to: {subAssembly.attachedToAirframe}
                </Typography>
              )}
              {subAssembly.attachmentLocation && (
                <Typography sx={{ color: "#ccc", mb: 1 }}>
                  Position: {subAssembly.attachmentLocation}
                </Typography>
              )}
            </>
          ) : (
            <>
              <Typography sx={{ color: "#ccc", mb: 1 }}>
                OffWing Type:{" "}
                {hasOffWingType ? formatOffWingTypeLabel(offWingType) : "—"}
              </Typography>
              {shopVisitReasonLabel && (
                <Typography sx={{ color: "#ccc", mb: 1 }}>
                  Inshop Reason: {shopVisitReasonLabel}
                </Typography>
              )}
            </>
          )}
        </Box>
      </CardContent>

      <CardActions>
        {canUpdate && canAccessMiniPackExport && onMiniPack && (
          <Tooltip title={MINI_PACK_ICON_TOOLTIP}>
            <IconButton
              size="small"
              onClick={() => onMiniPack(subAssembly)}
              sx={{ color: "hsl(19 101% 51% / 1)" }}
              aria-label="Configure mini-pack PDF"
            >
              <PictureAsPdfIcon />
            </IconButton>
          </Tooltip>
        )}
        {canUpdate && (
          <IconButton
            size="small"
            onClick={() => onEdit(subAssembly)}
            sx={{ color: "hsl(19 101% 51% / 1)" }}
          >
            <EditIcon />
          </IconButton>
        )}
        {canDelete && !attached && (
          <IconButton
            size="small"
            onClick={() => onDelete(subAssembly)}
            sx={{ color: "#f44336" }}
          >
            <DeleteIcon />
          </IconButton>
        )}
      </CardActions>
    </Card>
  );
}
